import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Typography, TextField, Button, IconButton, Checkbox, Avatar, Tabs, Tab, Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Menu } from "@mui/material";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { API_ENDPOINTS, getData, postData } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../LoadingContext";
import "./Users.css";

// --- Helper for Avatar Initials ---
const getInitials = (name) => {
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    if (parts.length > 1) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    return "";
};

function CustomTabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return { id: `simple-tab-${index}`, "aria-controls": `simple-tabpanel-${index}` };
}

function ManageUsers() {
    const [users, setUsers] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [minLoadTimeElapsed, setMinLoadTimeElapsed] = useState(false);
    const [error, setError] = useState(null);

    const [searchTextValue, setSearchTextValue] = useState("");
    const [appliedSearchTerm, setAppliedSearchTerm] = useState("");

    const [currentPage, setCurrentPage] = useState(0);
    const [usersPerPage, setUsersPerPage] = useState(10);

    const [tabValue, setTabValue] = useState(0);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

    const showSpinner = isLoadingData || !minLoadTimeElapsed;

    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const openUserMenu = Boolean(userMenuAnchorEl);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const navigate = useNavigate();

    // --- Fetch Users from API ---
    const fetchUsers = useCallback(async () => {
        setIsLoadingData(true);
        setMinLoadTimeElapsed(false);
        setError(null);

        const timer = setTimeout(() => setMinLoadTimeElapsed(true), 3000);

        try {
            const params = {};
            if (appliedSearchTerm) params.search = appliedSearchTerm;

            const data = await getData(API_ENDPOINTS.getUsersList, params);

            if (data.status === "SUCCESS" && data.result) {
                const mappedUsers = data.result.map((customer) => ({
                    id: customer.customer_id,
                    name: `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
                    email: customer.email || "N/A",
                    avatar: customer.pic || undefined,
                    mobile_num: customer.mobile_num,
                    city: customer.city || null,
                    state: customer.state || null,
                }));
                setUsers(mappedUsers);
            } else {
                setError(data.message || "Failed to fetch users.");
            }
        } catch (err) {
            setError("An error occurred while fetching users.");
        } finally {
            setIsLoadingData(false);
        }

        return () => clearTimeout(timer);
    }, [appliedSearchTerm]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // --- Pagination ---
    const paginatedUsers = useMemo(() => {
        const startIndex = currentPage * usersPerPage;
        return users.slice(startIndex, startIndex + usersPerPage);
    }, [users, currentPage, usersPerPage]);

    const totalPages = Math.ceil(users.length / usersPerPage);

    const isAllSelected = useMemo(() => users.length > 0 && users.every((user) => selectedUserIds.includes(user.id)), [users, selectedUserIds]);
    const isIndeterminate = useMemo(() => selectedUserIds.length > 0 && selectedUserIds.length < users.length, [selectedUserIds, users]);

    // --- Handlers ---
    const handlePageChange = (event, newPage) => setCurrentPage(newPage);
    const handleUsersPerPageChange = (event) => { setUsersPerPage(event.target.value); setCurrentPage(0); };
    const handleTabChange = (event, newValue) => setTabValue(newValue);

    const handleUserCheckboxChange = useCallback((userId, isChecked) => {
        setSelectedUserIds((prev) => isChecked ? [...prev, userId] : prev.filter((id) => id !== userId));
    }, []);

    const handleSelectAllChange = useCallback((event) => {
        if (event.target.checked) setSelectedUserIds(users.map((user) => user.id));
        else setSelectedUserIds([]);
    }, [users]);

    const handleDeleteSelectedClick = () => setOpenDeleteConfirm(true);
    const handleDeleteConfirmClose = () => setOpenDeleteConfirm(false);

    const handleDeleteConfirmed = useCallback(async () => {
        handleDeleteConfirmClose();
        if (selectedUserIds.length === 0) return;

        try {
            const response = await postData(API_ENDPOINTS.deleteMultipleUsers, { customer_ids: selectedUserIds });
            if (response.status === "SUCCESS") {
                setUsers((prev) => prev.filter((user) => !selectedUserIds.includes(user.id)));
                setSelectedUserIds([]);
            } else {
                setError(response.message || "Failed to delete users.");
            }
        } catch (err) {
            setError("An error occurred while deleting users.");
        }
    }, [selectedUserIds]);

    const handleSearchTextChange = (e) => setSearchTextValue(e.target.value);
    const handleSearchKeyPress = (e) => { if (e.key === "Enter") { setAppliedSearchTerm(searchTextValue); setCurrentPage(0); } };
    const handleClearSearch = () => { setSearchTextValue(""); setAppliedSearchTerm(""); setCurrentPage(0); };

    const renderLocation = (city, state) => city && state ? `${city}, ${state}` : city || state || "---";

    const handleMoreOptionsClick = (event, userId) => { setUserMenuAnchorEl(event.currentTarget); setSelectedUserId(userId); };
    const handleUserMenuClose = () => { setUserMenuAnchorEl(null); setSelectedUserId(null); };

    const handleViewProfile = useCallback((userId) => {
        navigate(`/app/user-details/${encryptId(userId)}`);
        handleUserMenuClose();
    }, [navigate]);

    return (
        <Box className="user-panel-container">
            <LoadingSpinner isLoading={showSpinner} />
            <Typography variant="h5" component="h1" gutterBottom className="main-title">User</Typography>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="All Users" {...a11yProps(0)} className="tab-label" />
                </Tabs>
            </Box>

            <CustomTabPanel value={tabValue} index={0} className="p-0">
                <Row className="control-bar mb-4 align-items-center">
                    <Col xs={12} md={7} lg={8} className="search-col">
                        <TextField
                            fullWidth
                            placeholder="Search user by name"
                            variant="outlined"
                            size="small"
                            value={searchTextValue}
                            onChange={handleSearchTextChange}
                            onKeyPress={handleSearchKeyPress}
                            InputProps={{
                                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                endAdornment: searchTextValue ? (
                                    <IconButton size="small" onClick={handleClearSearch} aria-label="clear search">
                                        <ClearIcon />
                                    </IconButton>
                                ) : null,
                                sx: { borderRadius: "8px" },
                            }}
                        />
                    </Col>
                    <Col xs={12} md={5} lg={4} className="d-flex justify-content-end">
                        {selectedUserIds.length > 0 && (
                            <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteSelectedClick} sx={{ ml: 2 }}>
                                Delete ({selectedUserIds.length})
                            </Button>
                        )}
                    </Col>
                </Row>

                {error && !showSpinner ? (
                    <Box sx={{ my: 4 }}><Alert severity="error">{error}</Alert></Box>
                ) : paginatedUsers.length === 0 && !showSpinner ? (
                    <Box sx={{ my: 4, textAlign: "center" }}>
                        <Typography variant="h6" color="textSecondary">No users found.</Typography>
                        {appliedSearchTerm && <Typography variant="body2" color="textSecondary">Try a different search term or clear the filter.</Typography>}
                    </Box>
                ) : (
                    <>
                        <div className="list-view-container">
                            <Table responsive hover className="user-list-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "4%" }}>
                                            <Checkbox size="small" checked={isAllSelected} indeterminate={isIndeterminate} onChange={handleSelectAllChange} />
                                        </th>
                                        <th style={{ width: "30%" }}>Name</th>
                                        <th style={{ width: "25%" }}>Email</th>
                                        <th style={{ width: "10%" }}>Mobile No.</th>
                                        <th>Location</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td>
                                                <Checkbox size="small" checked={selectedUserIds.includes(user.id)} onChange={(e) => handleUserCheckboxChange(user.id, e.target.checked)} />
                                            </td>
                                            <td>
                                                <div className="user-name-cell">
                                                    <Avatar src={user.avatar} alt={user.name} sx={{ width: 32, height: 32 }}>
                                                        {!user.avatar && getInitials(user.name)}
                                                    </Avatar>
                                                    <span className="user-name-text">{user.name}</span>
                                                </div>
                                            </td>
                                            <td>{user.email}</td>
                                            <td>{user.mobile_num}</td>
                                            <td>{renderLocation(user.city, user.state)}</td>
                                            <td className="text-end">
                                                <IconButton size="small" onClick={(e) => handleMoreOptionsClick(e, user.id)}>
                                                    <MoreVertIcon />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={userMenuAnchorEl}
                                                    open={openUserMenu && selectedUserId === user.id}
                                                    onClose={handleUserMenuClose}
                                                    PaperProps={{ sx: { boxShadow: "none", border: "1px solid #ddd" } }}
                                                >
                                                    <MenuItem onClick={() => handleViewProfile(selectedUserId)}>View Profile</MenuItem>
                                                </Menu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {!showSpinner && users.length > 0 && (
                            <Box className="pagination-controls" display="flex" justifyContent="end" alignItems="center" mt={2}>
                                <div className="items-per-page" style={{ display: "flex", alignItems: "center" }}>
                                    <Select value={usersPerPage} onChange={handleUsersPerPageChange} size="small">
                                        {[5, 10, 25, 50].map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                    </Select>
                                    <Typography variant="body2" sx={{ ml: 1 }}>per page</Typography>
                                </div>

                                <Typography variant="body2" sx={{ mx: 2 }}>Page {currentPage + 1} of {totalPages}</Typography>

                                <IconButton onClick={() => handlePageChange(null, Math.max(currentPage - 1, 0))} disabled={currentPage === 0}><KeyboardArrowLeft /></IconButton>
                                <IconButton onClick={() => handlePageChange(null, Math.min(currentPage + 1, totalPages - 1))} disabled={currentPage >= totalPages - 1}><KeyboardArrowRight /></IconButton>
                            </Box>
                        )}
                    </>
                )}
            </CustomTabPanel>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteConfirm} onClose={handleDeleteConfirmClose}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete {selectedUserIds.length} selected user(s)? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" autoFocus>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ManageUsers;
